import {
    SimplePresentationRunner,
    SimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {gsap} from "gsap";
import {Game} from "../Game";
import {Mental2SymbolStates, SYMBOL} from "../enums/Enum";
import {SymbolContainer} from "../reels/SymbolContainer";
import {GameUtils} from "../utils/GameUtils";
import {IFinalEvaluatedArea} from "../server/Data";
import {Symbol} from "../reels/Symbol";
import {SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {Config} from "../config/Config";
import {Sound} from "../sound/Sound";
import {TimelineSprite} from "../../core/animation/TimelineSprite";
import {NudgeSymbol} from "../reels/NudgeSymbol";


export class XHoleRunner implements SimplePresentationRunner {
    private _flowStateController: SimplePresentationRunnerController;

    shouldRun(): boolean {
        return Game.model.isXHoleActive();
    }

    start(flowStateController: SimplePresentationRunnerController): void {
        this._flowStateController = flowStateController;
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.add(() => {
            flowStateController.setSkipCallback(() => {
                this.onSkip(tl);
            });
        });
        tl.add(this.playXHole());
        tl.add(() => {
            Game.foregroundElements.waysCounter.updatePossibleWaysCounter(false, true, true);
        })
    }

    public playXHole(): gsap.core.Timeline {
        const xHoleTl: gsap.core.Timeline = new gsap.core.Timeline();
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const suckInTl: gsap.core.Timeline = new gsap.core.Timeline();
        const suckInContainer: PIXI.Container = new PIXI.Container();
        const xHoleSymContainer: SymbolContainer = GameUtils.getSymbol(2, 1);
        const xHoleBloodSuckEffect: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("xHoleBloodSuckEffect"));
        const xHolePos: PIXI.Point = GameUtils.getSymbolPos(2, 1);
        //xHoleBloodSuckEffect.position.set(350, 298);
        xHoleBloodSuckEffect.position.set(350, xHolePos.y + xHoleSymContainer.symbols[0].position.y);
        xHoleBloodSuckEffect.alpha = 0;
        const SymbolSuckSequanceArr: number [] = [6, 9, 8, 3, 10, 4, 12, 13, 14, 2, 1, 0];
        Game.effectsView.addChild(suckInContainer, xHoleBloodSuckEffect);
        if (Game.model.fireFrameCount < 4) {
            xHoleTl.to(this, {duration: 0.5}, 0);//delay
        }

        tl.add(() => {
            this.changeNudgeState(true);
        });
        tl.add([
            () => {
                xHoleBloodSuckEffect.alpha = 1;
            },
            GameUtils.createTimelineForAnimation(xHoleBloodSuckEffect, 0, "animation", false)
        ], 0);
        const reelWiseSymArr: SymbolContainer[][] = [];
        SymbolSuckSequanceArr.forEach((id: number, index: number) => {
            const reelId: number = Math.floor(id / 3);
            const rowId: number = id % 3;
            if (Game.model.data.reels[reelId][rowId] === SYMBOL.S2 || Game.model.data.reels[reelId][rowId] === SYMBOL.S1 || Game.model.data.reels[reelId][rowId] === SYMBOL.DEAD) {
                return;
            }
            const symbolContainerCopy: PIXI.Container = this.copySymbolContainer(reelId, rowId);
            suckInContainer.addChild(symbolContainerCopy);
            suckInTl.to(symbolContainerCopy, {duration: 0.4, angle: 360 * 5}, 0.2 + (index + 1) * 0.01);
            suckInTl.to(symbolContainerCopy.scale, {duration: 0.2, x: 0.8, y: 0.8}, 0.35 + (index * 0.02));

            const originalSymbolContainer: SymbolContainer = this.addShuffledSymbol(reelId, rowId);
            reelWiseSymArr[reelId] = reelWiseSymArr[reelId] || [];
            reelWiseSymArr[reelId].push(originalSymbolContainer);
        });

        const spitTl: gsap.core.Timeline = this.spit(xHoleBloodSuckEffect, reelWiseSymArr);

        suckInContainer.pivot.set(suckInContainer.width * 0.5, 100);
        suckInContainer.position.set(360, 316);
        suckInTl.to(suckInContainer, {duration: 0.5, angle: 360}, 0.38);
        suckInTl.to(suckInContainer.scale, {duration: 0.1, x: 0.8, y: 0.8}, 0.4);
        suckInTl.to(suckInContainer.scale, {duration: 0.3, x: 0, y: 0}, 0.56);
        suckInTl.to(suckInContainer, {duration: 0.1, alpha: 0, y: 0}, 0.8);
        suckInTl.add(() => {
            Game.backgroundElements.stopFlickerAnim();
        });

        tl.add([suckInTl,
            () => {
                Game.soundPlayer.playSound(Sound.X_HOLE_SUCK);
                Game.backgroundElements.playFlickerAnim(true);
            }], 0);

        tl.add(spitTl, 0.7);

        tl.add(() => {
            suckInContainer.children.forEach((symbolContainer: PIXI.DisplayObject) => {
                (symbolContainer as PIXI.Container).children.forEach((sym: PIXI.DisplayObject) => {
                    (sym as Symbol).removeSymbol();
                })
            });
            suckInContainer.parent?.removeChild(suckInContainer);
            xHoleBloodSuckEffect.parent?.removeChild(xHoleBloodSuckEffect);
        });
        tl.add(() => {
            this.convertToWild(xHoleSymContainer);
        });
        xHoleTl.add(tl);
        return xHoleTl;
    }

    public resizeReels(reelId: number, prevY: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        let symWithAllSplits: Symbol[] = [];
        const isActiveXHolePresent: boolean = Game.model.isXHoleActive() && reelId === 2;
        Game.reelView.reelModels[reelId].getAllVisibleStopSymbols().forEach((symData: SimpleReelModelSymbolData) => {
            const symContainer: SymbolContainer = (<SymbolContainer>symData.positionable);
            symContainer.symbols.forEach((sym: Symbol) => {
                symWithAllSplits.push(sym);
            });
        });
        let totalCount: number = symWithAllSplits.length;
        let availableHeight: number = Config.REEL_HEIGHT[reelId] - 99;//isSpecialSymbolReel ? isEnhancer ? 198 * 2.5 : 198 * 2 : 198 * 3;
        if (isActiveXHolePresent) {
            availableHeight = availableHeight - 99;
            totalCount = totalCount - 1;
        }
        let eachSymbolHeight: number = availableHeight / totalCount;
        let incrementalY: number = 0;
        for (let i: number = 0; i < symWithAllSplits.length; i++) {
            const symbol: Symbol = symWithAllSplits[i];
            let height: number = eachSymbolHeight;
            if (isActiveXHolePresent && symbol.symbolName === SYMBOL.X_HOLE) {
                height = 99;
            }

            const actualHeight: number = Config.AVAILABLE_SYMBOL_SIZES.reduce((prev: number, curr: number) => {
                return (Math.abs(curr - height) < Math.abs(prev - height) ? curr : prev);
            });
            let state: Mental2SymbolStates = GameUtils.getReSizeState(actualHeight);
            if (actualHeight !== height) {
                symbol.scale.y = height / actualHeight;
            }
            let parentY: number = symbol.parent.position.y;
            let y: number = incrementalY - parentY - (99 - (height * 0.5)) + prevY;
            if (reelId === 2) {
                y += 99;
            }
            incrementalY = incrementalY + height;
            tl.add(() => {
                symbol.setState(state);
                symbol.currentHeight = height;
                symbol.onResize();
            }, 0);
            tl.to(symbol, {duration: 0.1, y: y}, 0);
        }
        return tl;
    }

    private convertToWild(xHoleSymContainer: SymbolContainer) {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.delay(0.2);
        const symArr: string[] = [];
        const data: IFinalEvaluatedArea[] = Game.model.gameData.finalEvaluatedAreaAfterXhole[2][1]
        for (let index: number = 0; index < data[0].size; index++) {
            symArr.push(SYMBOL.WILD);
        }

        tl.add(() => {
            const initialY: number = xHoleSymContainer.symbols[0].y - (xHoleSymContainer.symbols[0].currentHeight * 0.5);
            Game.effectsView.playFlashEffectForXHole();
            xHoleSymContainer.xHoleToWild(symArr);
            xHoleSymContainer.symbols.forEach((symbol, subRowId: number) => {
                symbol.position.y = initialY + (symbol.currentHeight * subRowId) + (symbol.currentHeight * 0.5);
            });
        });
        tl.to({}, {duration: 0.1});
        tl.add(() => {
            this.onComplete();
        });
    }

    private changeNudgeState(shouldApplyDark: boolean, reelId?: number) {
        if (reelId) {
            const rowId: number = reelId === 2 ? 3 : 2;
            const nudgeSymbol: NudgeSymbol = <NudgeSymbol>GameUtils.getSymbol(reelId, rowId).symbols[0];
            shouldApplyDark ? nudgeSymbol.setNoWinState(reelId) : nudgeSymbol.setNoWinToNormalState(reelId);
        } else {
            if (Game.reelView.reelModels[1].isNudgeReel) {
                const nudgeSymbol: NudgeSymbol = <NudgeSymbol>GameUtils.getSymbol(1, 2).symbols[0];
                shouldApplyDark ? nudgeSymbol.setNoWinState(1) : nudgeSymbol.setNoWinToNormalState(1);
            }
            if (Game.reelView.reelModels[3].isNudgeReel) {
                const nudgeSymbol: NudgeSymbol = <NudgeSymbol>GameUtils.getSymbol(3, 2).symbols[0];
                shouldApplyDark ? nudgeSymbol.setNoWinState(3) : nudgeSymbol.setNoWinToNormalState(3);
            }
        }
    }

    private handleNudge(reelId: number): void {
        if (Game.reelView.reelModels[reelId].isNudgeReel) {
            const rowId: number = reelId === 2 ? 3 : 2;
            const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelId, rowId);
            const nudgeSym: NudgeSymbol = <NudgeSymbol>symbolContainer.symbols[0];
            nudgeSym.updateFinalMultiplier(Game.model.gameData.multiplierOnReels[reelId]);
            let symContainer: SymbolContainer = GameUtils.getSymbol(reelId, 0);
            symContainer && (symContainer.visible = true);
            symContainer = GameUtils.getSymbol(reelId, 1);
            symContainer && (symContainer.visible = true);
            symContainer = GameUtils.getSymbol(reelId, 2);
            symContainer?.parent?.addChild(symContainer);
        }
    }

    private addShuffledSymbol(reelId: number, rowId: number) {
        const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelId, rowId);
        symbolContainer.angle = 0;
        symbolContainer.scale.set(1);
        const symArr: string[] = [];
        const xHoleEvalArea: IFinalEvaluatedArea[][][] = Game.model.gameData.finalEvaluatedAreaAfterXhole;
        const deadRevealMultiplier: number[] = [];
        if (!Game.reelView.reelModels[reelId].isNudgeReel) {
            xHoleEvalArea[reelId][rowId].forEach((finalEvalArea: IFinalEvaluatedArea) => {
                const multiplier: number = (Game.model.xHoleMultiplier[reelId] && Game.model.xHoleMultiplier[reelId][rowId]) || 1;
                for (let size: number = 0; size < finalEvalArea.size / multiplier; size++) {
                    symArr.push(finalEvalArea.symTo);
                }
                if (finalEvalArea.wasDead) {
                    deadRevealMultiplier.push(finalEvalArea.multiplier);
                }
            });
        }
        if (symArr.length) {
            symbolContainer.xHoleSplit(symArr, deadRevealMultiplier);
        }

        if (symbolContainer.isTopHalf) {
            symbolContainer.hideTopHalf(true);
        }

        symbolContainer.alpha = 0;
        if (!Game.reelView.reelModels[reelId].isNudgeReel) {
            symbolContainer.visible = true;
        }

        return symbolContainer;
    }

    private spit(xHoleBloodSuckEffect: PIXI.spine.Spine, reelWiseSymArr: SymbolContainer[][]): gsap.core.Timeline {
        const spitTl: gsap.core.Timeline = new gsap.core.Timeline();
        const reelOrder: number[] = [3, 4, 0, 1, 2];
        const outDuration: number = 0.3;
        const xHoleSymContainer: SymbolContainer = GameUtils.getSymbol(2, 1);
        const xHolePosY: number = GameUtils.getSymbolPos(2, 1).y + xHoleSymContainer.symbols[0].position.y;
        const enhancerOpenedArr: boolean[] = [false, Game.model.fireFrameCount >= 4, Game.model.fireFrameCount >= 8, Game.model.fireFrameCount >= 6, false];
        spitTl.add(() => {
            for (let reelId = 0; reelId < Game.model.gameData.reels.length; reelId++) {
                const bufferSymbolTop: SymbolContainer = GameUtils.getSymbol(reelId, -1);
                if (bufferSymbolTop) {
                    bufferSymbolTop.visible = false;
                }
                if (reelId === 2) {
                    const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelId, 0);
                    if (symbolContainer.isTopHalf) {
                        symbolContainer.hideTopHalf(true);
                    }
                }
                if (!enhancerOpenedArr[reelId]) {
                    const bufferSymbolBottom: SymbolContainer = GameUtils.getSymbol(reelId, Game.model.gameData.reels[reelId].length);
                    if (bufferSymbolBottom) {
                        bufferSymbolBottom.visible = false;
                    }
                } else {
                    const enhancerSymbol: SymbolContainer = GameUtils.getSymbol(reelId, Game.model.gameData.evaluatedArea[reelId].length - 1);
                    enhancerSymbol.hideBottomHalfs(false);
                }
            }
            Game.reelView.removeMask();
        });
        reelOrder.forEach((reelOrderId: number) => {
            let midY: number = xHolePosY;
            if (!reelWiseSymArr[reelOrderId]) {
                return;
            }
            const reelOutTl: gsap.core.Timeline = new gsap.core.Timeline();
            reelOutTl.add(GameUtils.createTimelineForAnimation(xHoleBloodSuckEffect, 0, "spit", false), 0.1);
            reelOutTl.add(() => {
                Game.backgroundElements.scaleTo(1.05, 0.01).add(() => {
                    Game.backgroundElements.scaleTo(1, 0.1);
                })
                Game.soundPlayer.playSound(Sound.X_HOLE_SPIT_OUT);
            }, 0.1);

            if (!Game.reelView.reelModels[reelOrderId].isNudgeReel) {
                reelWiseSymArr[reelOrderId].forEach((originalSymbolContainer: SymbolContainer) => {
                    reelOutTl.to(originalSymbolContainer, {duration: 0.1, alpha: 1}, 0.1);
                    if (reelOrderId === 1 || reelOrderId === 3) {
                        midY -= 99;
                    }
                    reelOutTl.fromTo(originalSymbolContainer, {
                        x: 360,
                        y: midY - (originalSymbolContainer.height * 0.5)
                    }, {
                        x: originalSymbolContainer.x,
                        y: originalSymbolContainer.y,
                        duration: outDuration
                    }, 0.1);
                    reelOutTl.fromTo(originalSymbolContainer.scale, {x: 0.1, y: 0.1}, {
                        x: 1.5,
                        y: 1.5,
                        ease: "power2.out",
                        duration: outDuration / 2 + 0.1,
                    }, 0.1);
                    reelOutTl.to(originalSymbolContainer.scale, {
                        x: 1,
                        y: 1,
                        ease: "power2.in",
                        duration: (outDuration / 2) + 0.1,
                    }, 0.2 + (outDuration / 2));

                    reelOutTl.add(() => {
                        this.playSplitAnim(originalSymbolContainer.reelId, originalSymbolContainer.rowId);
                        gsap.to({}, {
                            duration: 0.1, onComplete: () => {
                                this.splitSymbols(originalSymbolContainer);
                            }
                        })
                    }, 0.6);
                });
            } else {
                reelOutTl.add(() => {
                    this.playSplitAnim(reelOrderId, 1, true);
                    this.changeNudgeState(false, reelOrderId);
                    gsap.to({}, {
                        duration: 0.1, onComplete: () => {
                            this.handleNudge(reelOrderId);
                        }
                    })
                }, 0.4);
            }
            reelOutTl.to({}, {duration: 0.1});
            spitTl.add(reelOutTl);
        });
        spitTl.add(() => {
            Game.reelView.addMask();
            for (let reelId = 0; reelId < Game.model.gameData.reels.length; reelId++) {
                const bufferSymbolTop: SymbolContainer = GameUtils.getSymbol(reelId, -1);
                if (bufferSymbolTop) {
                    bufferSymbolTop.visible = true;
                }
                if (!enhancerOpenedArr[reelId]) {
                    const bufferSymbolBottom: SymbolContainer = GameUtils.getSymbol(reelId, Game.model.gameData.reels[reelId].length);
                    if (bufferSymbolBottom) {
                        bufferSymbolBottom.visible = true;
                    }
                } else {
                    const enhancerSymbol: SymbolContainer = GameUtils.getSymbol(reelId, Game.model.gameData.evaluatedArea[reelId].length - 1);
                    enhancerSymbol.hideBottomHalfs(true);
                }
            }
        });
        return spitTl;
    }

    private playSplitAnim(reelId: number, rowId: number, isNudgePos: boolean = false) {
        const pos: PIXI.Point = GameUtils.getSymbolPos(reelId, rowId);
        const xHoleSplitTls: TimelineSprite = GameUtils.createTimelineSprite("xhole_win_blast/xhole_win_blast_000", 30, pos, true);
        Game.effectsView.addChildAt(xHoleSplitTls, 0);
        xHoleSplitTls.blendMode = PIXI.BLEND_MODES.ADD;
        if (isNudgePos) {
            xHoleSplitTls.position.y -= 49 + 64;
        }
        xHoleSplitTls.name = "xHoleSpit _" + reelId + "_" + rowId;
        xHoleSplitTls.getAnimationAutoShowHide(true, true).add(() => {
            xHoleSplitTls.parent?.removeChild(xHoleSplitTls);
        });
    }

    private splitSymbols(symbolContainer: SymbolContainer): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const reelId: number = symbolContainer.reelId;
        const rowId: number = symbolContainer.rowId;
        const xHoleEvalArea: IFinalEvaluatedArea[][][] = Game.model.gameData.finalEvaluatedAreaAfterXhole;
        const symArr: string[] = [];
        const maxSplitThreshold: number = 8;
        const maxSplitPerSymbolCanBe: number = maxSplitThreshold / xHoleEvalArea[reelId][rowId].length;
        let allSplitsAtIndex: number = 0;
        xHoleEvalArea[reelId][rowId].forEach((finalEvalArea: IFinalEvaluatedArea) => {
            const currentSize: number = (finalEvalArea.multiplier * finalEvalArea.multiplierOccurrence) + (finalEvalArea.size - finalEvalArea.multiplierOccurrence);
            //allSplitsAtIndex += finalEvalArea.size;
            allSplitsAtIndex += currentSize;
            const toBeSize: number = Math.min(maxSplitPerSymbolCanBe, finalEvalArea.size);
            for (let size: number = 0; size < toBeSize; size++) {
                symArr.push(finalEvalArea.symTo);
            }
        });
        const deadRevealMultiplier: number[] = [];
        //multiplier taken from finalEvaluatedArea as dead symbol will not shuffle by xHole
        Game.model.gameData.finalEvaluatedArea[reelId][rowId].forEach((finalEvaluatedArea: IFinalEvaluatedArea) => {
            if (finalEvaluatedArea.wasDead) {
                deadRevealMultiplier.push(finalEvaluatedArea.multiplier);
            }
        });
        symbolContainer.xHoleSplit(symArr, deadRevealMultiplier);

        let resizeTl: gsap.core.Timeline = new gsap.core.Timeline();
        if (this.isEnhancerExpanded(reelId)) {
            tl.add(() => {
                resizeTl = this.resizeReels(reelId, 0)
                //this.resizeReels(reelId, 0)
            });
        }
        if (Game.model.gameData.evaluatedArea[reelId][rowId] !== SYMBOL.DEAD) {
            tl.add(() => {
                resizeTl.add(() => {
                    symbolContainer.createXHoleMul(allSplitsAtIndex);
                });
            });
        }
        return tl;
    }

    private isEnhancerExpanded(reelId: number): boolean {
        let isExpanded: boolean = false;
        if (reelId > 0 && reelId < 4) {
            const enhancerIndex: number = reelId - 1;
            if (Game.model.enhancerFeatures[enhancerIndex] === SYMBOL.X_SPLIT || Game.model.enhancerFeatures[enhancerIndex] === SYMBOL.X_WAYS) {
                isExpanded = true;
            }
        }
        return isExpanded;
    }

    private copySymbolContainer(reelId: number, rowId: number): PIXI.Container {
        const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelId, rowId);
        symbolContainer.visible = false;
        const symbolContainerCopy: PIXI.Container = new PIXI.Container();
        symbolContainerCopy.position.set(symbolContainer.x, symbolContainer.y);
        symbolContainer.symbols.forEach((sym: Symbol, index: number) => {
            const newSymbol: Symbol = symbolContainer.createSymbol(sym.symbolName, Mental2SymbolStates.NORMAL, index, false);
            newSymbol.position.set(sym.x, sym.y);
            newSymbol.scale.set(sym.scale.x, sym.scale.y);
            newSymbol.pivot.set(sym.pivot.x, sym.pivot.y);
            newSymbol.setResizeState(sym.state.tracks[0].animation.name);
            newSymbol.currentHeight = sym.currentHeight;
            let multiplier: number = 1;
            if (Game.model.gameData.finalEvaluatedArea[reelId][rowId][index]) {
                multiplier = Game.model.gameData.finalEvaluatedArea[reelId][rowId][index].multiplier;
                if (multiplier !== 1) {
                    newSymbol.addDeadMultiplier(multiplier);
                }
            }
            symbolContainerCopy.addChild(newSymbol);
        });
        if (reelId === 1 || reelId === 3) {
            symbolContainerCopy.y += 99;
        }
        if (reelId === 2) {
            symbolContainerCopy.y -= 99;
        }
        symbolContainerCopy.visible = !Game.reelView.reelModels[reelId].isNudgeReel;
        return symbolContainerCopy;
    }

    private onComplete(): void {
        this._flowStateController.finish();
    }

    private onSkip(tl: gsap.core.Timeline): void {
        tl.timeScale(20);
    }
}